import { ReactElement } from 'react';
import { Controller, Loader, IDynamicLoader } from '@tencent/workbench';
import { FORM_CONFIGS } from '@framework/workbench/drawer/ui/text/border/config';
import { AngleFrom, DragAngleCircleType, Form, NumberInputFrom } from '@tencent/wb-ext-form';
import { FormItemName, IMultiDiffValue, INPUT_PROPS_CALLBACK_KEY } from '@framework/workbench/drawer/constants';
import { IBorderFeature } from './border-interface';
import { canvasToPt, ptToCanvas } from '@src/slide/infrastructure/module/independent/unit';
import {
    LINEAR_GRADIENT,
    RADIAL_GRADIENT,
    RECT_GRADIENT,
    SHAPE_GRADIENT,
} from '@framework/workbench/drawer/ui/text/fill/config';
import { alphaToOpacity } from '@framework/workbench/drawer/utils';
import { ANGLE_MAX } from '../../../common/config';
import { WIDTH_MAX } from '@tencent/drawing-drawer-components/lib/constants';

@Controller({ formItems: FORM_CONFIGS })
export class FontBorderController {
    constructor(@Loader(IBorderFeature) private loader: IDynamicLoader<IBorderFeature>) {}

    private get feature() {
        return this.loader.instance!;
    }

    @Form(FormItemName.FONT_BORDER_SELECT, 'content')
    public renderBorderContent() {
        return this.feature.renderBorderContent();
    }

    @Form(FormItemName.FONT_BORDER_LINE, 'content')
    public renderBorderLine(): ReactElement {
        return this.feature.renderBorderLine();
    }


    @Form(FormItemName.FONT_BORDER_WIDTH, 'value')
    public getWidth(): number | IMultiDiffValue {
        const widthObj = { width: canvasToPt(this.feature.width) };
        return this.feature.getShownValue(INPUT_PROPS_CALLBACK_KEY.TEXT_BORDER, widthObj);
    }

    @Form(FormItemName.FONT_BORDER_WIDTH, 'onChange')
    public changeWidth(value: number, from: NumberInputFrom): void {
        const { width } = this.feature.getValidValue({ width: value }, from, WIDTH_MAX);
        this.feature.handleBorderWidthChange(ptToCanvas(width));
    }

    @Form(FormItemName.FONT_BORDER_COLOR, 'content')
    public getColorSelectButton() {
        return this.feature.getColorSelectButton();
    }

    @Form(FormItemName.FONT_BORDER_OPACITY, 'value')
    public getOpacity(): number | IMultiDiffValue {
        const opacityObj = { opacity: this.feature.getOpacityFromColor() };
        return this.feature.getShownValue(INPUT_PROPS_CALLBACK_KEY.TEXT_BORDER, opacityObj);
    }

    @Form(FormItemName.FONT_BORDER_OPACITY, 'onChange')
    public changeOpacity(value: number, dragEnd: boolean, from: NumberInputFrom): void {
        const { opacity } = this.feature.getValidValue({ opacity: value }, from);
        this.feature.handleOpacityChange(opacity, dragEnd, from);
    }

    // 渐变填充 - 线性|径向
    @Form(FormItemName.FONT_BORDER_GRADUAL_LINEAR_GRADIENT, 'value')
    public getGradualFillLinear(): boolean {
        return this.feature.borderGradualValues.curGradualType === LINEAR_GRADIENT;
    }

    @Form(FormItemName.FONT_BORDER_GRADUAL_LINEAR_GRADIENT, 'onClick')
    public changeGradualFillLinear() {
        this.feature.handleGradualTypeChange(LINEAR_GRADIENT);
    }

    @Form(FormItemName.FONT_BORDER_GRADUAL_RADIAL_GRADIENT, 'value')
    public getGradualFillRadial(): boolean {
        return this.feature.borderGradualValues.curGradualType === RADIAL_GRADIENT;
    }

    @Form(FormItemName.FONT_BORDER_GRADUAL_RADIAL_GRADIENT, 'onClick')
    public changeGradualFillRadial() {
        this.feature.handleGradualTypeChange(RADIAL_GRADIENT);
    }

    @Form(FormItemName.FONT_BORDER_GRADUAL_RECT_GRADIENT, 'value')
    public getGradualFillRect(): boolean {
        return this.feature.borderGradualValues.curGradualType === RECT_GRADIENT;
    }

    @Form(FormItemName.FONT_BORDER_GRADUAL_RECT_GRADIENT, 'onClick')
    public changeGradualFillRect() {
        this.feature.handleGradualTypeChange(RECT_GRADIENT);
    }

    @Form(FormItemName.FONT_BORDER_GRADUAL_SHAPE_GRADIENT, 'value')
    public getGradualFillShape(): boolean {
        return this.feature.borderGradualValues.curGradualType === SHAPE_GRADIENT;
    }

    @Form(FormItemName.FONT_BORDER_GRADUAL_SHAPE_GRADIENT, 'onClick')
    public changeGradualFillShape() {
        this.feature.handleGradualTypeChange(SHAPE_GRADIENT);
    }

    @Form(FormItemName.FONT_BORDER_GRADUAL_ANGLE, 'value')
    public getGradualFillAngle(): number | IMultiDiffValue {
        const gradualAngleObj = { gradualAngle: this.feature.borderGradualValues.curAngle! };
        return this.feature.getShownValue(INPUT_PROPS_CALLBACK_KEY.TEXT_BORDER, gradualAngleObj);
    }

    @Form(FormItemName.FONT_BORDER_GRADUAL_ANGLE, 'onChange')
    public changeGradualFillAngle(value: number, from: AngleFrom, dragState?: DragAngleCircleType) {
        const { gradualAngle } = this.feature.getValidValue({ gradualAngle: value }, from, ANGLE_MAX);
        const dragEnd = !dragState || dragState === DragAngleCircleType.STOP;
        this.feature.handleGradualAngleChange(gradualAngle % 360, from, dragEnd);
    }

    @Form(FormItemName.FONT_BORDER_GRADUAL_DIRECTION, 'content')
    public getGradualFillDirectContent() {
        return this.feature.renderGradualDirection();
    }

    @Form(FormItemName.FONT_BORDER_GRADUAL_BAR, 'content')
    public renderGradualFill() {
        return this.feature.renderGradualFill();
    }

    @Form(FormItemName.FONT_BORDER_GRADUAL_OPACITY, 'value')
    public getGradualFillOpacity(): number | IMultiDiffValue {
        const color = this.feature.borderGradualValues.activeColor;
        if (!color) {
            return 0;
        }
        const gradualOpacityObj = { gradualOpacity: alphaToOpacity(color.alpha) };
        return this.feature.getShownValue(INPUT_PROPS_CALLBACK_KEY.TEXT_BORDER, gradualOpacityObj);
    }

    @Form(FormItemName.FONT_BORDER_GRADUAL_OPACITY, 'onChange')
    public changeGradualFillOpacity(value: number, dragEnd?: boolean, from?: NumberInputFrom) {
        const { gradualOpacity } = this.feature.getValidValue({ gradualOpacity: value }, from);
        this.feature.handleGradualOpacityChange(gradualOpacity, from, dragEnd);
    }

    @Form(FormItemName.FONT_BORDER_GRADUAL_ANGLE_TEXT, 'shouldDisabled')
    @Form(FormItemName.FONT_BORDER_GRADUAL_ANGLE, 'shouldDisabled')
    public shouldAngleDisabled(): boolean {
        return this.feature.shouldAngleDisabled();
    }

    @Form(FormItemName.FONT_BORDER_GRADUAL_DIRECTION, 'shouldDisabled')
    @Form(FormItemName.FONT_BORDER_GRADUAL_DIRECTION_TEXT, 'shouldDisabled')
    public shouldDirectionDisabled(): boolean {
        return this.feature.borderGradualValues.curGradualType === SHAPE_GRADIENT;
    }
}
